import React, { useState } from "react";
import styled from "styled-components";
import { Button, Dropdown } from "semantic-ui-react";

import { t } from "../../../constant/translations";
import { CalendarInput } from "../../common/calendar-input";

const Form = styled.div`
  border: 1px solid #e9ebee;
  border-radius: 14px;
  padding: 22px 24px;
  margin-bottom: 14px;
  background: #fff;
  box-shadow: 0 1px 0 rgba(20, 30, 50, 0.02);
`;

const Title = styled.div`
  font-weight: 800;
  font-size: 16px;
  margin-bottom: 18px;
  letter-spacing: -0.2px;
`;

const FieldLabel = styled.div`
  font-size: 11px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #78716c;
  margin: 16px 0 8px;
  font-weight: 700;
`;

const FieldRow = styled.div`
  display: flex;
  gap: 14px;
  & > div {
    flex: 1;
    min-width: 0;
  }
`;

const InputWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  & .suffix {
    position: absolute;
    right: 12px;
    color: #78716c;
    font-weight: 700;
    pointer-events: none;
  }
`;

const Input = styled.input`
  width: 100%;
  border: 1px solid #e7e5e4;
  border-radius: 10px;
  padding: 11px 14px;
  font-size: 15px;
  font-family: inherit;
  color: #1c1917;
  background: #fff;
  &:focus {
    outline: none;
    border-color: #2185d0;
    box-shadow: 0 0 0 3px rgba(33, 133, 208, 0.14);
  }
`;

const NotesArea = styled.textarea`
  width: 100%;
  border: 1px solid #e7e5e4;
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 15px;
  font-family: inherit;
  color: #1c1917;
  background: #fff;
  resize: vertical;
  min-height: 96px;
  line-height: 1.5;
  &:focus {
    outline: none;
    border-color: #2185d0;
    box-shadow: 0 0 0 3px rgba(33, 133, 208, 0.14);
  }
`;

const CalWrap = styled.div`
  & .ui.input {
    width: 100%;
  }
  & .ui.input > input {
    width: 100%;
    border: 1px solid #e7e5e4 !important;
    border-radius: 10px !important;
    padding: 11px 14px !important;
    font-size: 15px !important;
  }
  & .ui.input > input:focus {
    border-color: #2185d0 !important;
    box-shadow: 0 0 0 3px rgba(33, 133, 208, 0.14) !important;
  }
`;

const CatDropdown = styled(Dropdown)`
  &.ui.dropdown {
    width: 100%;
    border: 1px solid #e7e5e4;
    border-radius: 10px;
    padding: 11px 14px !important;
    font-size: 15px !important;
    line-height: 1.4 !important;
    min-height: 0 !important;
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
`;

export const EditDetailsForm = ({
  service,
  create,
  categories,
  onSave,
  onCancel,
  saving,
}) => {
  const [name, setName] = useState(service ? service.name : "");
  const [category, setCategory] = useState(
    service ? service.category : categories[0] || ""
  );
  const [amount, setAmount] = useState(service ? String(service.amount) : "");
  const [start, setStart] = useState(
    service && service.start_date ? service.start_date : ""
  );
  const [end, setEnd] = useState(
    service && service.end_date ? service.end_date : ""
  );
  const [notes, setNotes] = useState(service ? service.notes || "" : "");

  function save() {
    const num = parseFloat(String(amount).replace(",", "."));
    onSave({
      name: (name || "").trim() || (service ? service.name : ""),
      category,
      amount: isNaN(num) ? service && service.amount : num,
      start_date: start || null,
      end_date: end || null,
      notes,
    });
  }

  return (
    <Form create={create}>
      <Title>
        {create
          ? t("contract.line.new_service")
          : t("contract.line.edit_details_full")}
      </Title>

      <FieldRow style={{ marginTop: 2 }}>
        <div style={{ flex: 1.3 }}>
          <FieldLabel>{t("contract.service_name")}</FieldLabel>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div style={{ flex: 1 }}>
          <FieldLabel>{t("contract.category")}</FieldLabel>
          <CatDropdown
            selection
            search
            value={category}
            options={categories.map((c) => ({ key: c, value: c, text: c }))}
            onChange={(_, d) => setCategory(d.value)}
          />
        </div>
      </FieldRow>

      <FieldRow style={{ marginTop: 4 }}>
        <div>
          <FieldLabel>{t("contract.line.amount_per_month")}</FieldLabel>
          <InputWrap>
            <Input
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputMode="decimal"
              placeholder="0,00"
            />
            <span className="suffix">€</span>
          </InputWrap>
        </div>
        <div>
          <FieldLabel>{t("contract.line.effective_from")}</FieldLabel>
          <CalWrap>
            <CalendarInput
              name="start"
              value={start || ""}
              onChange={(e) => setStart(e.target.value)}
            />
          </CalWrap>
        </div>
        <div>
          <FieldLabel>
            {t("contract.line.end_label")}{" "}
            <span
              style={{
                textTransform: "none",
                fontWeight: 600,
                color: "#78716c",
              }}
            >
              ({t("contract.line.no_end")})
            </span>
          </FieldLabel>
          <CalWrap>
            <CalendarInput
              name="end"
              value={end || ""}
              onChange={(e) => setEnd(e.target.value)}
            />
          </CalWrap>
        </div>
      </FieldRow>

      <FieldLabel>{t("contract.notes")}</FieldLabel>
      <NotesArea
        rows={3}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
      />

      <Actions>
        <Button basic size="small" onClick={onCancel}>
          {t("contract.line.cancel")}
        </Button>
        <Button primary size="small" loading={saving} onClick={save}>
          {create
            ? t("contract.line.add_service")
            : t("contract.line.save_details")}
        </Button>
      </Actions>
    </Form>
  );
};
